import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export function SignupPage() {
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const [loading, setLoading] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [orgSize, setOrgSize] = useState('medium');
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgName || !adminName || !adminEmail || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      const result = await api.auth.signup({
        orgName, orgSize: orgSize as 'small' | 'medium' | 'large' | 'enterprise',
        adminEmail, adminName, password, planType: 'trial',
      });
      setUser(result.user);
      toast.success('Account created! Welcome to NOETED.');
      navigate('/onboarding');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Signup failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-teal">NOETED</h1>
          <p className="mt-1 text-sm text-muted-foreground">Healthcare Compliance Intelligence</p>
          <h2 className="mt-4 text-lg font-semibold">Create your account</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 rounded-lg border border-border bg-card p-6">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Organization Name</label>
            <Input value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="e.g., Pacific Dental Insurance" className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Organization Size</label>
            <Select value={orgSize} onValueChange={(v) => setOrgSize(v ?? '')}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="small">Small (1-50 employees)</SelectItem>
                <SelectItem value="medium">Medium (51-500)</SelectItem>
                <SelectItem value="large">Large (501-5000)</SelectItem>
                <SelectItem value="enterprise">Enterprise (5000+)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Your Name</label>
            <Input value={adminName} onChange={(e) => setAdminName(e.target.value)} placeholder="Sarah Chen" className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Email</label>
            <Input type="email" autoComplete="off" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="cco@yourorg.com" className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Password</label>
            <Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 8 characters" className="mt-1" />
          </div>

          <Button type="submit" className="w-full bg-teal text-white hover:bg-teal/90" disabled={loading}>
            {loading ? 'Creating account...' : 'Create Account'}
          </Button>

          <p className="text-center text-xs text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="text-teal hover:underline">Log in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
